/**
 * Screen Wake Lock — keep the device screen ON while audio is playing.
 *
 * Field notes (Sep 2026 · Lenovo Tab K11 incident)
 * ------------------------------------------------
 * Before this rewrite, the hook had two bugs that manifested as "tablet
 * plays 1 song, then screen dims and Chrome throttles the next track":
 *
 *   1. On the browser-emitted `release` event we DID call `acquire()`
 *      again — but ONLY if the tab was already visible at that instant.
 *      Android's Chrome auto-releases the wake lock the moment the tab
 *      becomes hidden (screen off / user swipes to home), which means the
 *      re-acquire branch never fired. When the tab came back to
 *      foreground, our `visibilitychange` listener checked
 *      `if (!lock) acquire()` — but `lock` was still a REFERENCE to the
 *      (now-released) old lock object, so `!lock` was false. We never
 *      re-acquired.
 *
 *   2. Some Android Chrome builds silently reject `wakeLock.request()`
 *      when the tab isn't visible. We were calling `acquire()` from the
 *      release-listener regardless of visibility.
 *
 * Both are fixed below by:
 *   - Nulling `lock` on release so `visibilitychange` correctly detects
 *     "no lock held".
 *   - Gating every `acquire()` call on `visibilityState === 'visible'`.
 *   - Additionally re-attempting on `pageshow` (Android Chrome sometimes
 *     fires that but not `visibilitychange` when returning from a deep
 *     sleep).
 *
 * Limitations (still true — set expectations honestly):
 *   - DOES fix: screen auto-lock while the app is foregrounded and playing.
 *   - DOES NOT fix: user manually backgrounding the app, phone-call
 *     interruption (OS pauses media session by design), hardware power
 *     button on some Android skins that force-suspend the whole tab.
 *   - For the "phone in pocket, screen locked" case we also rely on the
 *     Web-Worker-backed watchdog in MusicContext to force-advance if
 *     playback stalls (see backgroundTimer.js).
 */
import { useEffect } from 'react';

export function useScreenWakeLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return undefined;

    let lock = null;
    let cancelled = false;

    const isVisible = () =>
      typeof document !== 'undefined' && document.visibilityState === 'visible';

    const acquire = async () => {
      if (cancelled || lock) return;
      // Chrome (Android) rejects wakeLock.request() when the tab isn't
      // visible — swallow the error rather than let it noisy-crash.
      if (!isVisible()) return;
      try {
        const l = await navigator.wakeLock.request('screen');
        if (cancelled) {
          // Racing with an unmount — release immediately.
          try { l.release(); } catch (_) { /* noop */ }
          return;
        }
        lock = l;
        // When the OS releases the lock (tab hidden, low power, etc.) drop
        // our reference so a subsequent visibility change can re-acquire.
        lock.addEventListener('release', () => {
          lock = null;
          // Re-acquire right away IF we're still supposed to be awake and
          // the tab is visible. When hidden, the visibility listener
          // below takes over.
          if (!cancelled && isVisible()) acquire();
        });
      } catch (err) {
        // NotAllowedError, SecurityError, etc. Log at warn — not error —
        // because the user is still able to keep the screen on manually.
        // eslint-disable-next-line no-console
        console.warn('[useScreenWakeLock] could not acquire:', err?.message || err);
      }
    };

    // Initial acquisition — deferred to the next tick so React finishes
    // the render pass before we touch the browser API.
    acquire();

    // Any of these events can indicate "we just came back to foreground";
    // some Android Chrome builds fire only one of the three, so we listen
    // to all of them and rely on `if (lock) return` inside acquire() for
    // idempotency.
    const onVisible = () => { if (isVisible() && !lock) acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);

    // Belt-and-suspenders: some Android skins (seen on Lenovo tablets) drop the
    // wake lock WITHOUT firing the `release` event, so neither the release
    // listener nor a visibility change ever re-acquires it and the screen
    // silently auto-locks mid-set. Poll every 15s and re-acquire if we've lost
    // the lock while still supposed to be awake and visible. acquire() is
    // idempotent (`if (lock) return`), so this is a no-op when healthy.
    const poll = setInterval(() => {
      if (!cancelled && !lock && isVisible()) acquire();
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onVisible);
      if (lock) {
        lock.release().catch(() => {});
        lock = null;
      }
    };
  }, [active]);
}
